import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Sparkles, ArrowRight, X } from 'lucide-react';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

/**
 * Banner shown when a user has only ONE role (renter OR mobilitier) and can
 * activate the SECOND role to unlock the dual-mode experience.
 *
 * Props:
 *   - target: 'mobilitier' | 'user'  (which secondary role to offer)
 */
const ActivateDualRoleBanner = ({ target }) => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(false);

  // Hide if user already has it (primary or secondary), is admin, or dismissed
  if (!user || dismissed) return null;
  if (user.role === 'admin') return null;
  if (user.role === target || user.secondary_role === target) return null;

  const copy = target === 'mobilitier'
    ? {
        eyebrow: 'PUBLICA TUS AUTOS',
        title: '¿Quieres también ganar dinero rentando tu auto?',
        body: 'Activa tu rol de Mobilitier y publica tus autos en la plataforma. Sin segundo registro, misma cuenta.',
        cta: 'Convertirme en Mobilitier',
        nextRoute: '/mobilitier/add-car',
      }
    : {
        eyebrow: 'RENTA AUTOS DE OTROS',
        title: '¿Quieres también rentar autos para tus viajes?',
        body: 'Activa tu rol de Renter y reserva autos de otros Mobilitiers cuando lo necesites.',
        cta: 'Activar modo Renter',
        nextRoute: '/search',
      };

  const activate = async () => {
    setLoading(true);
    try {
      await api.post('/users/me/add-role', { role: target });
      toast.success('¡Rol activado!');
      if (refreshUser) await refreshUser();
      navigate(copy.nextRoute);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'No se pudo activar el rol';
      toast.error(typeof msg === 'string' ? msg : 'Error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="mb-5 border-0 overflow-hidden bg-gradient-to-r from-ink to-ink-800 text-white relative" data-testid={`dual-role-banner-${target}`}>
      <CardContent className="p-5 sm:p-6 relative">
        <button
          onClick={() => setDismissed(true)}
          className="absolute top-3 right-3 text-white/40 hover:text-white"
          aria-label="Cerrar"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-start gap-4 flex-wrap">
          <div className="w-11 h-11 rounded-xl bg-lime grid place-items-center shrink-0">
            <Sparkles className="w-5 h-5 text-ink" />
          </div>
          <div className="flex-1 min-w-[200px]">
            <div className="text-[10px] font-comfortaa font-bold tracking-[0.22em] text-lime">{copy.eyebrow}</div>
            <h3 className="mt-1 font-comfortaa font-bold text-lg sm:text-xl">{copy.title}</h3>
            <p className="mt-1 text-sm text-white/65 font-light">{copy.body}</p>
          </div>
          <Button
            onClick={activate}
            disabled={loading}
            className="rounded-full h-11 px-5 bg-lime hover:bg-lime-500 text-ink font-comfortaa font-bold text-sm"
            data-testid={`activate-${target}-btn`}
          >
            {loading ? 'Activando...' : copy.cta}
            {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ActivateDualRoleBanner;
